/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:no-non-null-assertion

import { DecimalPipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { BuilderUserContext, GtoEntryPoint } from '@gh/builder-shared';
import { BuilderPermission } from '@gh/builder-shared-data';
import { CanLeaveDialog, createDialogRoute, DEFAULT_AUTOSAVE_INTERVALS, HelpID } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  Confirmation,
  DialogInput,
  DialogService,
  NotificationService,
  UploadContext,
  WaitingService,
  yesAnswer,
} from '@gh/core-ui';
import { filter$, map$, switchMap$, tap$ } from '@gh/rx/operators';
import { isNil } from 'lodash';
import { Observable } from 'rxjs';
import { GraphicalTakeOffCommandService } from '../../graphical-take-off-command.service';
import { GraphicalTakeOffFormState } from '../../graphical-take-off-form.service';
import {
  getGraphicalTakeOffHousePlanViewId,
  getGraphicalTakeOffTopId,
  getGraphicalTakeOffTopType,
  getGraphicalTakeOffTopViewId,
  GraphicalTakeOffBusinessService,
  GraphicalTakeOffDataBoxVisibility,
  GraphicalTakeOffHousePlanView,
  GraphicalTakeOffTop,
  GTO_MEASUREMENT_UNITS_IN_FT,
  GtoShortcutScope,
} from '../../resources';
import { GraphicalTakeOffCanvasComponent } from '../canvas/graphical-take-off-canvas.component';
import { graphicalTakeOffChangeDialogRoute } from '../change';
import { GraphicalTakeOffCloneComponent } from '../clone';
import { getLocalValue, setLocalValue } from '@gh/core-util';
import { TimerEvent } from '@gh/common-internal-shared';

type GraphicalTakeOffEstimateTop = {
  topId: ID;
  count: number;
  isFromGto: boolean;
  value: {
    uomCode: string;
    value: any;
  };
};

export type GraphicalTakeOffMainConfig = {
  title: string;
  entryPoint: GtoEntryPoint;
  id: ID;
  priceBookId?: ID;
  quickQuote: boolean;
  allTops?: GraphicalTakeOffEstimateTop[];
  gFolderId: ID;
  isDisabledGto?: boolean;
};

const GRAPHICAL_AUTOSAVE_LOCAL_KEY = 'graphical:autosave';

/**
 * Main GTO component.
 */
@Component({
  selector: 'gh-graphical-take-off-main',
  template: require('./graphical-take-off-main.component.html'),
  styles: [require('./graphical-take-off-main.component.scss')],
  providers: [
    GraphicalTakeOffBusinessService,
    GraphicalTakeOffFormState,
    GraphicalTakeOffCommandService,
    UploadContext,
  ],
  // tslint:disable-next-line:use-host-property-decorator
  host: {
    tabIndex: '0',
  },
})
export class GraphicalTakeOffMainComponent implements AfterViewInit, DialogInput<GraphicalTakeOffMainConfig>, CanLeaveDialog {
  input: GraphicalTakeOffMainConfig;

  /**
   * Canvas element
   * See: http://paperjs.org/reference/paperscope/#setup-element
   *
   * @type {ElementRef}
   */
  @ViewChild('gtoCanvas', { static: false }) canvas: GraphicalTakeOffCanvasComponent;
  @ViewChild('canvasContainer', { static: false }) canvasContainerElement: ElementRef;

  /**
   * Show level mode menu?
   *
   * @type {boolean}
   */
  levelMode: boolean = false;

  /**
   * Show crop mode menu?
   *
   * @type {boolean}
   */
  cropMode: boolean = false;

  /**
   * Measurement status
   *
   * @type {string}
   */
  status: string;
  autosaveIntervals = DEFAULT_AUTOSAVE_INTERVALS;
  currentAutosaveInterval = getLocalValue(GRAPHICAL_AUTOSAVE_LOCAL_KEY, '30m');
  isChangeTipsDisable: boolean;
  isFormEditable: boolean;

  get isSaveDisabled(): boolean {
    return this.levelMode || this.cropMode || this.form.isSetScaleToolActive || !this.allowEdit || !this.form.dirty;
  }

  /**
   * Constructor
   *
   * @param {UploadContext} uploadContext - Upload context
   * @param {WaitingService} waitingService - Waiting service
   * @param {DialogService} dialogService - Dialog service
   * @param {GraphicalTakeOffBusinessService} gtoService GTO service
   * @param {MatDialogRef<GraphicalTakeOffMainComponent>} mainDialogRef - Dialog ref
   */
  constructor(
    private uploadContext: UploadContext,
    private decimalPipe: DecimalPipe,
    private waitingService: WaitingService,
    private dialogService: DialogService,
    private translations: TranslationService,
    private userContext: BuilderUserContext,
    private notifications: NotificationService,
    private gtoService: GraphicalTakeOffBusinessService,
    private mainDialogRef: MatDialogRef<GraphicalTakeOffMainComponent>,
    private commands: GraphicalTakeOffCommandService,
    public form: GraphicalTakeOffFormState,
  ) {
    this.isChangeTipsDisable = false;
    this.isFormEditable = false;
  }

  isViewDirty(): boolean {
    return this.form.dirty;
  }

  canDeactivate(): Observable<boolean> {
    return this.commands.completeFlow.run({
      trigger: 'deactivate',
      form: this.form,
      value: this.form.submitValue,
    }).toFinalState().pipe(map$((state: any) => state.confirmation));
  }

  @HostListener('document:contextmenu', ['$event'])
  menuContext(e: MouseEvent): boolean {
    e.preventDefault();
    return false;
  }

  /**
   * Init component
   *
   * @returns {void}
   */
  ngAfterViewInit(): void {
    this.gtoService.setCanvasElement(this.canvas.canvasElement);
    this.gtoService.setPaperScope(this.canvas.scope);
    this.commands.initCanvas(this.canvas.scope);

    this.commands.build(this.allowEdit);
    this.form.hasSelectedTop$.subscribe((value) => {
      setTimeout(() => {
        this.isChangeTipsDisable = !value;
      },         0);
    });
    this.form.editable$.subscribe((value) => {
      setTimeout(() => {
        this.isFormEditable = value;
      },         0);
    });
  }

  get view(): GraphicalTakeOffHousePlanView {
    return this.form.currentView;
  }

  get helpId(): HelpID {
    return this.scaleMode ? 'estimating/gto/set-scale' : 'estimating/gto';
  }

  /**
   * Is menu disabled?
   *
   * @returns {boolean} - Is menu disabled?
   */
  get menuDisabled(): boolean {
    return this.levelMode || this.cropMode;
  }

  get scaleMode(): boolean {
    return this.form.scaleMode;
  }

  /**
   * Is delete view disabled?
   *
   * @returns {boolean} - Is delete view disabled?
   */
  get deleteViewDisabled(): boolean {
    return !this.view || this.levelMode || this.cropMode || !this.allowEdit;
  }

  /**
   * Is edit menu disabled?
   *
   * @returns {boolean} - Is edit menu disabled?
   */
  get editMenuDisabled(): boolean {
    if (!this.view) {
      return true;
    }

    const picture = this.view.picture;
    return !picture || this.form.scaleMode || !this.allowEdit;
  }

  /**
   * Is scale results disabled?
   *
   * @returns {boolean}
   */
  get scaleResultsDisabled(): boolean {
    if (!this.view || !this.view.picture) {
      return true;
    }

    return !(this.view.picture.scaleWidth || this.view.picture.scaleHeight);
  }

  /**
   * Get measurement default units
   *
   * @returns {string}
   */
  get measurementLengthUnits(): string {
    return (this.view.measurementDefault || '').toLowerCase();
  }

  /**
   * Is imperial units?
   *
   * @returns {boolean}
   */
  get isImperialUnits(): boolean {
    return this.gtoService.isImperialUnits(this.view);
  }

  get scaleWidthImperial(): string {
    if (!this.view.picture || !this.view.picture.scaleWidth) {
      return '';
    }

    const widthFt = Math.floor(this.view.picture.scaleWidth);
    const widthIn = this.decimalPipe.transform(
      (this.view.picture.scaleWidth - widthFt) * GTO_MEASUREMENT_UNITS_IN_FT,
      '1.2-2',
    );

    return this.translations.get('lbl_ft_in', { ft: `${widthFt}`, in: `${widthIn}` });
  }

  get scaleHeightImperial(): string {
    if (!this.view.picture || !this.view.picture.scaleHeight) {
      return '';
    }

    const heightFt = Math.floor(this.view.picture.scaleHeight);
    const heightIn = this.decimalPipe.transform(
      (this.view.picture.scaleHeight - heightFt) * GTO_MEASUREMENT_UNITS_IN_FT,
      '1.2-2',
    );

    return this.translations.get('lbl_ft_in', { ft: `${heightFt}`, in: `${heightIn}` });
  }

  get allowEdit(): boolean {
    let permission: boolean = false;

    switch (this.input.entryPoint) {
      case GtoEntryPoint.JE:
        permission = this.userContext.hasPermission(BuilderPermission.Estimating.JobEstimate.EDIT);
        break;
      case GtoEntryPoint.HP:
        permission = this.userContext.hasPermission(BuilderPermission.Estimating.HouseEstimate.Estimate.CREATE_EDIT);
        break;
      case GtoEntryPoint.QQ:
        if (this.input.isDisabledGto) {
          return !this.input.isDisabledGto;
        }
        permission = this.userContext.hasPermission(BuilderPermission.Sales.Leads.EDIT);
        break;
      default:
        break;
    }

    return permission;
  }

  /**
   * Toggle level mode
   *
   * @returns {void}
   */
  toggleLevelMode(): void {
    this.levelMode = !this.levelMode;
  }

  /**
   * Toggle crop mode
   *
   * @returns {void}
   */
  toggleCropMode(): void {
    this.cropMode = !this.cropMode;
    if (this.cropMode) {
      this.commands.startCroping();
    } else {
      this.commands.stopCroping();
    }
  }

  onStartScaling(): void {
    if (!this.commands.tryToFinalizeMeasurement()) {
      return;
    }

    this.commands.startScaling();
  }

  onStopScaling(): void {
    this.commands.stopScaling();
  }

  onApplyScaling(): void {
    this.commands.applyScaling();
  }

  /**
   * Overlay
   *
   * @param change Checkbox change
   * @returns {void}
   */
  overlay(change: any): void {
    this.commands.updateUserPreference({overLay: change.checked})
    this.commands.setOverlay(change.checked);
  }

  /**
   * Show no databoxes
   *
   * @returns {void}
   */
  showNoDataboxes(): void {
    this.commands.updateUserPreference({hideShowDataBoxes: GraphicalTakeOffDataBoxVisibility.ShowNoDataboxes})
    this.commands.setDataBoxVisibility(GraphicalTakeOffDataBoxVisibility.ShowNoDataboxes);
  }

  /**
   * Show all
   *
   * @returns {void}
   */
  showAll(): void {
    this.commands.updateUserPreference({hideShowDataBoxes: GraphicalTakeOffDataBoxVisibility.ShowAll})
    this.commands.setDataBoxVisibility(GraphicalTakeOffDataBoxVisibility.ShowAll);
  }

  /**
   * Show top only
   *
   * @returns {void}
   */
  showTopOnly(): void {
    this.commands.updateUserPreference({hideShowDataBoxes: GraphicalTakeOffDataBoxVisibility.ShowTopOnly})
    this.commands.setDataBoxVisibility(GraphicalTakeOffDataBoxVisibility.ShowTopOnly);
  }

  /**
   * Show selection only
   *
   * @returns {void}
   */
  showSelection(): void {
    this.commands.updateUserPreference({hideShowDataBoxes: GraphicalTakeOffDataBoxVisibility.ShowSelection})
    this.commands.setDataBoxVisibility(GraphicalTakeOffDataBoxVisibility.ShowSelection);
  }

  /**
   * Change TOP
   *
   * @returns {void}
   */
  changeTop(): void {
    if (!this.commands.tryToFinalizeMeasurement()) {
      return;
    }

    const sourceTopView = this.form.currentTopView;
    if (isNil(sourceTopView)) {
      return;
    }

    const sourceType = getGraphicalTakeOffTopType(sourceTopView);
    const sourceTopId = getGraphicalTakeOffTopViewId(sourceTopView);
    const tops = this.form.tops.filter((top) =>
      sourceType === getGraphicalTakeOffTopType(top) && getGraphicalTakeOffTopId(top) !== sourceTopId);
    const dialogRef = this.dialogService.openWithInput(graphicalTakeOffChangeDialogRoute, {
      topView: sourceTopView,
      tops,
    });

    dialogRef
      .afterClosed().pipe(
        tap$(() => this.commands.focus(GtoShortcutScope.Canvas)),
        filter$(Boolean))
      .subscribe((data: any) => {
        this.commands.moveTopTo(data.top, data.option);
      });
  }

  /**
   * Clone
   *
   * @returns {void}
   */
  clone(): void {
    const { selectedTop, selectedMeasurement } = this.form;
    if (isNil(selectedTop) || isNil(selectedMeasurement)) {
      return;
    }

    const type = getGraphicalTakeOffTopType(selectedTop);
    const tops = this.form.tops.filter((top: GraphicalTakeOffTop) => {
      const requiredType = 'LENGTH' === type ? 'AREA' : 'LENGTH';
      return requiredType === getGraphicalTakeOffTopType(top);
    });
    const dialogRef = this.dialogService.open(GraphicalTakeOffCloneComponent, {
      data: {
        tops,
        top: selectedTop,
      },
    });

    dialogRef
      .afterClosed().pipe(
        tap$(() => this.commands.focus(GtoShortcutScope.Canvas)),
        filter$(Boolean))
      .subscribe((topId: ID) => {
        const top = this.form.getTop(topId);
        if (top) {
          this.commands.cloneTop(selectedTop, selectedMeasurement, top);
        }
      });
  }

  import(): void {
    this.commands.import();
  }

  onViewChange(viewId: ID): void {
    this.commands.requestView(viewId);
  }

  /**
   * Scroll view.
   * Get next view from views array and draw it
   *
   * @returns {void}
   */
  goToNextView(validate: boolean = true): void {
    const { views, currentView } = this.form;

    if (validate && !this.commands.tryToFinalizeMeasurement()) {
      return;
    }

    const viewIndex = views.indexOf(currentView);
    const nextIndex = viewIndex === views.length - 1 ? 0 : viewIndex + 1;

    const id = getGraphicalTakeOffHousePlanViewId(views[nextIndex]);
    if (id) {
      this.commands.requestView(id);
    }
  }

  /**
   * Delete view
   *
   * @returns {void}
   */
  deleteView(): void {
    if (!this.commands.tryToFinalizeMeasurement()) {
      return;
    }

    const { currentView, views } = this.form;

    const viewId = getGraphicalTakeOffHousePlanViewId(currentView);
    const isLastView = views.length === 1;

    if (!viewId) {
      return;
    }

    this.dialogService
      .confirmation({
        messageId: isLastView ? 'msg_gto_delete_last_view' : 'msg_gto_delete_view',
        buttons: [Confirmation.Yes, Confirmation.Cancel],
      }).pipe(
      filter$((yesAnswer)),
      switchMap$(() =>
        this.gtoService.deleteHousePlanView(
          this.input.id,
          viewId,
          { version: this.view.version },
          this.input.entryPoint,
        ).pipe(this.waitingService.operator())))
      .subscribe(() => {
        this.commands.removeView(viewId);

        if (isLastView) {
          this.import();
        } else {
          this.goToNextView(false);
        }
      });
  }

  /**
   * Cancel
   *
   * @returns {void}
   */
  onCancel(): void {
    this.canDeactivate()
      .pipe(filter$(Boolean))
      .subscribe(() => this.mainDialogRef.close());
  }

  onBackClick(): void {
    if (this.levelMode || this.cropMode || this.form.isSetScaleToolActive) {
      return;
    }
    this.onCancel();
  }

  /**
   * Update view
   *
   * @param {boolean} closeWindow - Close GTP?
   * @param {boolean} saveState - Save all views?
   * @returns {void}
   */
  onSave(): void {
    this.commands.completeFlow.run({
      trigger: 'process',
      form: this.form,
      value: this.form.submitValue,
    });
  }

  /**
   * Change status
   *
   * @param {string} value - Status value
   * @returns {void}
   */
  getStatus(value: string): void {
    this.status = value;
  }

  onAutoSave(event: TimerEvent): void {
    event.waitFor(this.commands.autoSave());
  }

  onChangeAutosaveInterval(interval: string): void {
    setLocalValue(GRAPHICAL_AUTOSAVE_LOCAL_KEY, interval);
  }
}

export const graphicalTakeOffMainDialogRoute = createDialogRoute({
  component: GraphicalTakeOffMainComponent,
  config: {
    disableClose: true,
  },
});
